import React, {
  Children,
  cloneElement,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import { useClickAway } from "ahooks";
import { createPortal } from "react-dom";
import "./index.less";

const DEFAULT_OFFSET = 8;
const DEFAULT_Z_INDEX = 1030;
const ANIMATION_MS = 180;

function toArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function assignRef(ref, value) {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  try {
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
  } catch (e) {
    // ignore
  }
}

function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => assignRef(ref, node));
  };
}

function callAll(...fns) {
  return (...args) => {
    fns.forEach((fn) => {
      if (typeof fn === "function") fn(...args);
    });
  };
}

function parsePlacement(placement, align) {
  const normalized = (placement || "").trim();
  if (!normalized) return { side: "bottom", align: align || "center" };
  const parts = normalized.split("-");
  return {
    side: parts[0] || "bottom",
    align: parts[1] || align || "center",
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getTransformOrigin(side, align) {
  const a = align || "center";
  if (side === "top") {
    if (a === "start") return "left bottom";
    if (a === "end") return "right bottom";
    return "center bottom";
  }
  if (side === "bottom") {
    if (a === "start") return "left top";
    if (a === "end") return "right top";
    return "center top";
  }
  if (side === "left") {
    if (a === "start") return "right top";
    if (a === "end") return "right bottom";
    return "right center";
  }
  if (a === "start") return "left top";
  if (a === "end") return "left bottom";
  return "left center";
}

/**
 * Popover
 *
 * Features:
 * - trigger: click | hover | focus | contextMenu (single or array)
 * - callbacks: onOpen, onClose, onOpenChange
 * - controlled/uncontrolled: open / defaultOpen
 * - placement: top|bottom|left|right with -start|-center|-end
 * - click away + re-click trigger closes by default
 * - enter/exit animation
 */
function Popover({
  children,
  content,
  anchorRef,
  getAnchorRect,
  open,
  defaultOpen = false,
  onOpen,
  onClose,
  onOpenChange,
  trigger = "click",
  placement = "bottom",
  align,
  offset = DEFAULT_OFFSET,
  zIndex = DEFAULT_Z_INDEX,
  className,
  overlayClassName,
  overlayStyle,
  disabled = false,
  closeOnClickAway = true,
  shouldCloseOnClickAway,
  openDelay = 80,
  closeDelay = 120,
  getPopupContainer,
}) {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen);
  const mergedOpen = isControlled ? !!open : uncontrolledOpen;

  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const rafRef = useRef(null);
  const exitTimerRef = useRef(null);

  const [mounted, setMounted] = useState(!!mergedOpen);
  const [animState, setAnimState] = useState(mergedOpen ? "entered" : "exited");
  const [positionStyle, setPositionStyle] = useState(null);

  const triggers = useMemo(() => toArray(trigger), [trigger]);
  const { side, align: align2 } = useMemo(
    () => parsePlacement(placement, align),
    [placement, align]
  );

  const close = useCallback(
    (reason, event) => {
      if (disabled) return;
      if (!mergedOpen) return;
      if (!isControlled) setUncontrolledOpen(false);
      if (typeof onOpenChange === "function") onOpenChange(false, { reason, event });
      if (typeof onClose === "function") onClose({ reason, event });
    },
    [
      disabled,
      mergedOpen,
      isControlled,
      onOpenChange,
      onClose,
      setUncontrolledOpen,
    ]
  );

  const openPopup = useCallback(
    (reason, event) => {
      if (disabled) return;
      if (mergedOpen) return;
      if (!isControlled) setUncontrolledOpen(true);
      if (typeof onOpenChange === "function") onOpenChange(true, { reason, event });
      if (typeof onOpen === "function") onOpen({ reason, event });
    },
    [disabled, mergedOpen, isControlled, onOpenChange, onOpen]
  );

  const toggle = useCallback(
    (reason, event) => {
      if (mergedOpen) close(reason, event);
      else openPopup(reason, event);
    },
    [mergedOpen, close, openPopup]
  );

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(
    (reason, event) => {
      clearTimers();
      openTimerRef.current = setTimeout(() => {
        openPopup(reason, event);
      }, Math.max(0, openDelay));
    },
    [clearTimers, openDelay, openPopup]
  );

  const scheduleClose = useCallback(
    (reason, event) => {
      clearTimers();
      closeTimerRef.current = setTimeout(() => {
        close(reason, event);
      }, Math.max(0, closeDelay));
    },
    [clearTimers, closeDelay, close]
  );

  // click away to close (default)
  useClickAway(
    (event) => {
      if (!closeOnClickAway) return;
      if (!mergedOpen) return;

      if (typeof shouldCloseOnClickAway === "function") {
        const shouldClose = shouldCloseOnClickAway(event);
        if (!shouldClose) return;
      }

      close("click-away", event);
    },
    [triggerRef, popupRef]
  );

  const updatePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const popupEl = popupRef.current;
    if (!popupEl) return;

    const anchorEl = anchorRef?.current || null;
    const anchorRectFromProps =
      typeof getAnchorRect === "function" ? getAnchorRect() : null;
    const anchorRect =
      anchorRectFromProps || (anchorEl ? anchorEl.getBoundingClientRect() : null);

    const baseRect = anchorRect || (triggerEl ? triggerEl.getBoundingClientRect() : null);
    if (!baseRect) return;

    const popupRect = popupEl.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const margin = 8;

    let top = 0;
    let left = 0;

    if (side === "top" || side === "bottom") {
      if (side === "bottom") top = baseRect.bottom + offset;
      else top = baseRect.top - popupRect.height - offset;

      if (align2 === "start") left = baseRect.left;
      else if (align2 === "end") left = baseRect.right - popupRect.width;
      else left = baseRect.left + (baseRect.width - popupRect.width) / 2;
    } else {
      if (side === "right") left = baseRect.right + offset;
      else left = baseRect.left - popupRect.width - offset;

      if (align2 === "start") top = baseRect.top;
      else if (align2 === "end") top = baseRect.bottom - popupRect.height;
      else top = baseRect.top + (baseRect.height - popupRect.height) / 2;
    }

    left = clamp(left, margin, viewportW - popupRect.width - margin);
    top = clamp(top, margin, viewportH - popupRect.height - margin);

    setPositionStyle({
      position: "fixed",
      top: Math.round(top),
      left: Math.round(left),
      zIndex,
      transformOrigin: getTransformOrigin(side, align2),
    });
  }, [side, align2, offset, zIndex, anchorRef, getAnchorRect]);

  // Mount/unmount with enter/exit animation
  useEffect(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (mergedOpen) {
      setMounted(true);
      setAnimState("entering");
      rafRef.current = requestAnimationFrame(() => {
        setAnimState("entered");
      });
      return;
    }

    if (!mounted) {
      setAnimState("exited");
      return;
    }

    setAnimState("exiting");
    exitTimerRef.current = setTimeout(() => {
      setMounted(false);
      setAnimState("exited");
    }, ANIMATION_MS);
  }, [mergedOpen, mounted]);

  useLayoutEffect(() => {
    if (!mounted) return;
    updatePosition();
  }, [mounted, updatePosition, content]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onWindowChange = () => updatePosition();
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);

    let ro;
    if (typeof ResizeObserver !== "undefined" && popupRef.current) {
      ro = new ResizeObserver(() => updatePosition());
      ro.observe(popupRef.current);
    }

    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
      if (ro) ro.disconnect();
    };
  }, [mounted, updatePosition]);

  useEffect(() => {
    return () => {
      clearTimers();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [clearTimers]);

  const child = useMemo(() => {
    const onlyChild = Children.count(children) === 1 ? Children.only(children) : null;
    if (onlyChild && React.isValidElement(onlyChild)) return onlyChild;
    return <span className="ui-popover__triggerWrap">{children}</span>;
  }, [children]);

  const triggerProps = useMemo(() => {
    const props = {
      ref: mergeRefs(child && child.ref, triggerRef),
      "aria-haspopup": "dialog",
      "aria-expanded": mergedOpen,
    };

    if (disabled) return props;

    if (triggers.includes("click")) {
      props.onClick = callAll(child.props && child.props.onClick, (e) => {
        toggle("click", e);
      });
    }

    if (triggers.includes("contextMenu")) {
      props.onContextMenu = callAll(child.props && child.props.onContextMenu, (e) => {
        e.preventDefault();
        toggle("contextMenu", e);
      });
    }

    if (triggers.includes("hover")) {
      props.onMouseEnter = callAll(child.props && child.props.onMouseEnter, (e) => {
        scheduleOpen("hover", e);
      });
      props.onMouseLeave = callAll(child.props && child.props.onMouseLeave, (e) => {
        scheduleClose("hover", e);
      });
    }

    if (triggers.includes("focus")) {
      props.onFocus = callAll(child.props && child.props.onFocus, (e) => {
        openPopup("focus", e);
      });
      props.onBlur = callAll(child.props && child.props.onBlur, (e) => {
        close("blur", e);
      });
    }

    return props;
  }, [
    child,
    disabled,
    mergedOpen,
    triggers,
    toggle,
    scheduleOpen,
    scheduleClose,
    openPopup,
    close,
  ]);

  const popupNode = mounted ? (
    <div
      ref={popupRef}
      className={cn(
        "ui-popover",
        overlayClassName,
        className,
        "ui-popover__anim",
        {
          "is-entering": animState === "entering",
          "is-entered": animState === "entered",
          "is-exiting": animState === "exiting",
        }
      )}
      style={{ ...positionStyle, ...overlayStyle }}
      data-side={side}
      data-align={align2}
      role="dialog"
      aria-hidden={!mergedOpen}
      onMouseEnter={(e) => {
        if (!triggers.includes("hover")) return;
        clearTimers();
        if (!mergedOpen) openPopup("hover", e);
      }}
      onMouseLeave={(e) => {
        if (!triggers.includes("hover")) return;
        scheduleClose("hover", e);
      }}
    >
      {typeof content === "function" ? content({ close }) : content}
    </div>
  ) : null;

  const container =
    typeof getPopupContainer === "function" ? getPopupContainer() : document.body;

  return (
    <>
      {cloneElement(child, triggerProps)}
      {popupNode && container ? createPortal(popupNode, container) : popupNode}
    </>
  );
}

export default memo(Popover);
export { Popover };
